import { useMutation } from "@tanstack/react-query";
import {
	createConfigurationId,
	updateConfiguration,
	addConfigurationToCart,
	updateSmallCart,
} from "../services/cartService";
import { validateConfiguration } from "../services/validationService";
import type { ComponentSelections } from "../utils/componentSelections";
import { getSelectionsForGroup } from "../utils/componentSelections";
import { SELECTION_TYPES } from "../types/viewmodel";
import { triggerCustomEvent } from "../utils/eventHelpers.ts";

const MESSAGE_LEVEL_ERROR = "error";
const UNABLE_TO_ADD_INVALID_PRODUCT_TYPE_KEY = "cart.product.unabletoaddinvalidproducttype";
const CART_ERROR_MISMATCH_CONFIGBUNDLE_KEY = "cart.error.mismatch.configbundle";


interface ProductGroupEntry {
	id: string;
	product?: { id: string } | null;
	products?: { id: string }[];
	selectionType: string;
}

interface UseAddToCartOptions {
	configBundleId: string;
	configurationId?: string;
	selectedComponents: ComponentSelections;
	productGroups: ProductGroupEntry[];
	incompatibleErrorMessage?: string;
	onSuccess?: (params: { renewalPeriod?: string }) => void;
	onError?: (error: string) => void;
}

export const useAddToCart = ({
	configBundleId,
	configurationId,
	selectedComponents,
	productGroups,
	incompatibleErrorMessage,
	onSuccess,
	onError,
}: UseAddToCartOptions) => {
	const addToCartMutation = useMutation({
		mutationFn: async (params?: { renewalPeriod?: string }) => {
			const renewalPeriod = params?.renewalPeriod;

			const products = productGroups
				.flatMap((group) => {
					if (group.selectionType === SELECTION_TYPES.ALWAYS_INCLUDED) {
						return group.product?.id
							? [{ matnr: group.product.id, productGroupId: group.id }]
							: [];
					}

					if (group.selectionType === SELECTION_TYPES.MULTIPLE) {
						const rawEntry = selectedComponents[group.id];
						if (rawEntry === undefined) {
							// Never touched -- fall back to configured defaults.
							const defaults = group.products ?? (group.product ? [group.product] : []);
							return defaults.map((p) => ({ matnr: p.id, productGroupId: group.id }));
						}
						const entries = getSelectionsForGroup(selectedComponents, group.id);
						return entries
							.filter((s) => s.productId !== null)
							.map((s) => ({ matnr: s.productId!, productGroupId: group.id }));
					}

					const entry = selectedComponents[group.id];
					const selection = Array.isArray(entry) ? undefined : entry;
					if (selection?.productId === null) {
						return [];
					}
					const productId = selection?.productId ?? group.product?.id;
					if (!productId) return [];
					return [{ matnr: productId, productGroupId: group.id }];
				});

			const validationResult = await validateConfiguration(
				products.map((p) => ({ id: p.matnr })),
			);
			if (!validationResult.isValid) {
				throw new Error(
					incompatibleErrorMessage ?? "Konfigurasjonen er ikke kompatibel"
				);
			}

			const savedConfigurationId = configurationId
				? await updateConfiguration(configurationId, configBundleId, products)
				: await createConfigurationId(configBundleId, products);

			const cartResponse = await addConfigurationToCart(
				configBundleId,
				savedConfigurationId,
				renewalPeriod
			);

			const invalidProductTypeMessage = cartResponse.messages.find(
				(message) => message.key === UNABLE_TO_ADD_INVALID_PRODUCT_TYPE_KEY
			);

			if (invalidProductTypeMessage) {
				throw new Error(invalidProductTypeMessage.message);
			}

			const productMismatch = cartResponse.messages.find(
				(message) => message.key === CART_ERROR_MISMATCH_CONFIGBUNDLE_KEY,
			);

			if (productMismatch) {
				throw new Error(productMismatch.message);
			}

			const errorMessages = cartResponse.messages.filter(
				(message) => message.level === MESSAGE_LEVEL_ERROR
			);

			if (errorMessages.length > 0) {
				throw new Error(
					errorMessages.map((msg) => msg.message).join(" ")
				);
			}

			return { cartResponse, renewalPeriod, configurationId: savedConfigurationId };
		},
		onSuccess: ({ cartResponse, renewalPeriod, configurationId}) => {
			updateSmallCart(cartResponse);

			triggerCustomEvent("openAddToCartPopup", {
				detail: {
					productId: configBundleId,
					renewalPeriod: renewalPeriod,
					configBundleConfigurationId: configurationId,
				},
			});

			onSuccess?.({ renewalPeriod });
		},
		onError: (error: Error) => {
			onError?.(error.message || "Failed to add to cart");
		},
	});

	return {
		addToCart: (params?: { renewalPeriod?: string }) => addToCartMutation.mutate(params),
		isAdding: addToCartMutation.isPending,
		error: addToCartMutation.error,
	};
};
